/* eslint-disable no-console */
import Highcharts from "highcharts/esm/highcharts.src"

import { useMemo } from "react"

import { EmptyState } from "src/components/EmptyState"
import { ParadiseBarChart } from "src/components/Paradise/ParadiseCharts/ParadiseBarChart"
import { ParadiseLineChart } from "src/components/Paradise/ParadiseCharts/ParadiseLineChart"
import { useParadiseDeviceReadings } from "src/components/Paradise/ParadiseDevices/ParadiseDeviceReadings/useParadiseDeviceReadings"
import { useParadiseGuestPlotBands } from "src/components/Paradise/useParadiseGuestPlotBands"
import {
  DeviceReadingType,
  IDeviceReadings,
} from "src/data/devices/types/deviceTypes"
import { TParadiseDevice } from "src/data/paradise/paradiseDevices/types/paradiseDeviceQueryTypes"
import { useFetchParadiseHome } from "src/data/paradise/paradiseHomes/queries/paradiseHomeQueries"
import { useFetchNoiseMonitoringPreset } from "src/data/profileSettings/queries/monitoringPresetQueries"
import { TTimezone } from "src/data/timezones/timezones"
import { getThresholdDataSeries } from "src/ui/Graphs/dataUtils"
import { TGraphZoomOption } from "src/ui/Graphs/graphTypes"

export function ParadiseDeviceReadingsGraph({
  device,
  type,
  dateRange,
  setDateRange,
  homeTimezone,
  timezone,
  zoom,
}: {
  device: TParadiseDevice
  type: DeviceReadingType
  dateRange: { startAt: Date; endAt: Date }
  setDateRange: (dateRange: { startAt: Date; endAt: Date }) => void
  homeTimezone: TTimezone
  timezone: TTimezone
  zoom?: TGraphZoomOption
}) {
  const timeDiff = dateRange.endAt.getTime() - dateRange.startAt.getTime()

  // Time constants in milliseconds
  const MS_PER_SECOND = 1000
  const MS_PER_MINUTE = MS_PER_SECOND * 60
  const MS_PER_HOUR = MS_PER_MINUTE * 60
  const MS_PER_DAY = MS_PER_HOUR * 24

  // Resolution thresholds and their corresponding settings
  const RESOLUTION_SETTINGS = [
    {
      threshold: MS_PER_DAY * 60, // 60 days
      resolution: 3600 * 2, // 2 hours
      showThresholds: false,
    },
    {
      threshold: MS_PER_DAY * 33, // 33 days
      resolution: 60 * 10, // 10 minutes
      showThresholds: true,
    },
    {
      threshold: MS_PER_DAY * 10, // 10 days
      resolution: 600, // 5 minutes
      showThresholds: true,
    },
    {
      threshold: MS_PER_DAY * 2, // 24 hours
      resolution: 300, // 5 minutes
      showThresholds: true,
    },
    {
      threshold: MS_PER_HOUR * 2, // 2 hours
      resolution: 60, // 1 minute
      showThresholds: true,
    },
  ]

  // Find the appropriate resolution settings
  const { resolution: timeResolutionSeconds, showThresholds } =
    RESOLUTION_SETTINGS.find(({ threshold }) => timeDiff > threshold) ?? {
      resolution: 1,
      showThresholds: true,
    } // Default to 1 second if no threshold matches

  const deviceReadings = useParadiseDeviceReadings({
    deviceId: device.device_id,
    type,
    dateRange,
    timeResolutionSeconds,
  })

  // Show guests if the graph is temperature, helping in debugging
  // thermostat integrations
  const guestPlotbands = useParadiseGuestPlotBands({
    homeId: device.home,
    dateRange,
    enabled: type === "temperature",
  })

  if (
    type === "motion_events" &&
    hasReadings(deviceReadings.motionReadings?.values)
  ) {
    return (
      <ParadiseBarChart
        data={deviceReadings.motionReadings.values}
        tooltip={{ unit: "" }}
        canExport
        zoom={zoom}
      />
    )
  }

  if (hasReadings(deviceReadings.dashboardReadings)) {
    return (
      <ParadiseLineChart
        data={deviceReadings.dashboardReadings}
        tooltip={{ unit: "" }}
        canExport
        smooth={false}
        zoomAxis={zoom}
        onZoom={setDateRange}
        timezone={timezone}
        loading={deviceReadings.isLoading}
      />
    )
  }

  if (hasReadings(deviceReadings.readings?.values)) {
    if (type === "sound_level") {
      return (
        <ParadiseSoundReadingsGraph
          device={device}
          deviceReadings={deviceReadings.readings}
          dateRange={dateRange}
          timezone={timezone}
          homeTimezone={homeTimezone}
          zoom={zoom}
          showThresholds={showThresholds}
          setDateRange={setDateRange}
          loading={deviceReadings.isLoading}
        />
      )
    }

    return (
      <ParadiseLineChart
        data={deviceReadings.readings.values}
        tooltip={{ unit: "" }}
        canExport
        smooth={false}
        xAxisPlotBands={guestPlotbands}
        zoomAxis={zoom}
        onZoom={setDateRange}
        timezone={timezone}
        loading={deviceReadings.isLoading}
      />
    )
  }

  return <EmptyState icon={<></>} title="No readings available" />
}

function hasReadings<Type extends unknown[]>(
  readings: Type | undefined
): readings is Type {
  if (readings && readings.length > 0) {
    return true
  }

  return false
}

function ParadiseSoundReadingsGraph({
  device,
  deviceReadings,
  dateRange,
  setDateRange,
  timezone,
  homeTimezone,
  zoom,
  showThresholds = true,
  loading,
}: {
  device: TParadiseDevice
  deviceReadings: IDeviceReadings
  dateRange: { startAt: Date; endAt: Date }
  setDateRange: (dateRange: { startAt: Date; endAt: Date }) => void
  timezone: TTimezone
  homeTimezone: TTimezone
  zoom?: TGraphZoomOption
  showThresholds?: boolean
  loading?: boolean
}) {
  const isOutdoors = device.placed_outdoors
  const useFetchHome = useFetchParadiseHome({
    homeId: device.home,
    options: {
      enabled: !!device.home,
    },
  })
  const home = useFetchHome.data
  const useFetchPreset = useFetchNoiseMonitoringPreset({
    orgId: home?.owner.id ?? "",
    noisePresetId: home?.noise_profile_id,
    enabled: !!home?.owner.id && !!home?.noise_profile_id,
  })
  const preset = useFetchPreset.data
  const thresholdConfig = isOutdoors
    ? preset?.outdoor_noise_threshold
    : preset?.indoor_noise_threshold

  const thresholdSeries = useMemo(() => {
    if (!thresholdConfig || !showThresholds) return undefined

    const thresholds: {
      value: number
      startHHMM?: string
      endHHMM?: string
    }[] = [
      {
        value: thresholdConfig.threshold,
      },
    ]
    if (thresholdConfig.quiet_hours_enabled && thresholdConfig.quiet_hours) {
      let [startHH, startMM] =
        thresholdConfig.quiet_hours.start_time_of_day.split(":")
      let [endHH, endMM] =
        thresholdConfig.quiet_hours.end_time_of_day.split(":")

      // If showing data in UTC then convert the threshold start and end to
      // what they are in UTC, this way we don't need to convert the data series
      if (timezone !== homeTimezone) {
        let result = getLocalHHMMInUTC(startHH, startMM, homeTimezone)
        startHH = result.HH
        startMM = result.MM
        result = getLocalHHMMInUTC(endHH, endMM, homeTimezone)
        endHH = result.HH
        endMM = result.MM
      }

      thresholds.push({
        value: thresholdConfig.quiet_hours.threshold,
        startHHMM: `${startHH}:${startMM}`,
        endHHMM: `${endHH}:${endMM}`,
      })
    }

    const series = getThresholdDataSeries(
      thresholds,
      {
        startDate: dateRange.startAt,
        endDate: dateRange.endAt,
      },
      timezone.tzCode,
      deviceReadings.values.map((reading) => ({
        ...reading,
        dateTimeUtc: reading.datetime,
      }))
    ) as Highcharts.SeriesLineOptions

    return series
  }, [
    thresholdConfig,
    showThresholds,
    dateRange.startAt,
    dateRange.endAt,
    homeTimezone,
    deviceReadings.values,
    timezone,
  ])

  const guestPlotbands = useParadiseGuestPlotBands({
    homeId: device.home,
    dateRange,
  })

  return (
    <ParadiseLineChart
      data={deviceReadings.values}
      tooltip={{ unit: "dB" }}
      canExport
      thresholdSeries={thresholdSeries}
      smooth={false}
      xAxisPlotBands={guestPlotbands}
      zoomAxis={zoom}
      onZoom={setDateRange}
      timezone={timezone}
      loading={loading}
    />
  )
}

function getUTCISOStringFromOffsetMinutes(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  offsetMinutes: number
): string {
  const utcMillis = Date.UTC(year, month - 1, day, hour, minute, second)
  const shiftedMillis = utcMillis - offsetMinutes * 60 * 1000
  return new Date(shiftedMillis).toISOString()
}

/**
 * Returns what HH and MM from the timezone represents in UTC
 */
function getLocalHHMMInUTC(
  HH: string | undefined,
  MM: string | undefined,
  homeTimezone: TTimezone
): { HH: string; MM: string } {
  const d = new Date() // Start with today to get DST correct
  const timeInUTC = getUTCISOStringFromOffsetMinutes(
    d.getFullYear(),
    d.getMonth() + 1,
    d.getDate(),
    parseInt(HH ?? "N/A"),
    parseInt(MM ?? "N/A"),
    0,
    homeTimezone.offsetMinutes
  )
  const time = timeInUTC.split("T")[1]

  const [convertedHH, convertedMM] = time?.split(":") ?? []
  return { HH: convertedHH ?? "", MM: convertedMM ?? "" }
}
